import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import type { Announcement, AnnouncementKind } from '@electrodavisan/shared';
import { api } from './api';

export const announcementKeys = {
  feed: ['announcements', 'feed'] as const,
  all: ['announcements', 'all'] as const,
};

interface AnnouncementPage {
  items: Announcement[];
  total: number;
  nextCursor: string | null;
}

export function useAnnouncements(search = '', enabled = true) {
  const auth = useAuth();
  return useAnnouncementArchive(false, search, enabled && auth.isAuthenticated, auth.user?.profile.sub);
}
export function useAllAnnouncements(enabled = true, search = '') {
  const auth = useAuth();
  return useAnnouncementArchive(true, search, enabled && auth.isAuthenticated, auth.user?.profile.sub);
}
function useAnnouncementArchive(admin: boolean, search: string, enabled: boolean, actor?: string) {
  return useInfiniteQuery({
    queryKey: [...(admin ? announcementKeys.all : announcementKeys.feed), actor, search],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.get<AnnouncementPage>(
      admin ? '/api/announcements/borradores/archivo' : '/api/announcements/archivo',
      { query: { limit: 20, search, cursor: pageParam } },
    ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
    staleTime: 30_000,
  });
}

export interface AnnouncementInput {
  title: string;
  body: string;
  kind: AnnouncementKind;
  publish: boolean;
  pinned: boolean;
}

/*
 * Tras cualquier cambio se invalidan LAS DOS listas.
 *
 * Publicar un borrador lo mueve de una a otra, asi que refrescar solo la que
 * se estaba mirando deja la otra con datos viejos: el anuncio recien publicado
 * no aparece en el canal hasta recargar la pagina.
 */
function useInvalidarAmbas() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: announcementKeys.feed });
    void queryClient.invalidateQueries({ queryKey: announcementKeys.all });
  };
}

export function useCreateAnnouncement() {
  const invalidar = useInvalidarAmbas();

  return useMutation({
    mutationFn: (input: AnnouncementInput) =>
      api.post<Announcement>('/api/announcements', input),
    onSuccess: invalidar,
  });
}

export function useUpdateAnnouncement() {
  const invalidar = useInvalidarAmbas();

  return useMutation({
    mutationFn: ({ id, ...cambios }: Partial<AnnouncementInput> & { id: string; expectedRevision: number; republish?: boolean }) =>
      api.patch<Announcement>(`/api/announcements/${id}`, cambios),
    onSuccess: invalidar,
  });
}

export function useDeleteAnnouncement() {
  const invalidar = useInvalidarAmbas();

  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`/api/announcements/${id}`),
    onSuccess: invalidar,
  });
}
